/**
 * AmbientMusic — simple, reliable implementation
 *
 * Uses HTMLAudioElement only (no Web Audio API).
 * - audio.muted = true/false  → hardware-level mute on iOS, synchronous
 * - audio.volume              → fade in/out
 * - visibilitychange          → pause/resume on tab switch (iOS 13+, all desktop)
 * - iOS autoplay: audio.play() called synchronously inside gesture handler
 */

import { useEffect, useRef } from 'react'

export function AmbientMusic({
  src,
  volume = 0.4,
  fadeInDuration = 3.0,
  enabled = true,
  muted = false,
}) {
  const audioRef   = useRef(null)
  const fadeRef    = useRef(null)
  const startedRef = useRef(false)
  const mutedRef   = useRef(muted)

  // ─── Mute / unmute ───────────────────────────────────────────────────────
  useEffect(() => {
    mutedRef.current = muted
    const audio = audioRef.current
    if (!audio) return

    clearFade()

    if (muted) {
      audio.muted = true
      audio.pause()
    } else if (startedRef.current) {
      audio.muted = false
      audio.play().then(() => fadeIn(audio)).catch(() => {})
    }
  }, [muted]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Core lifecycle ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return

    const audio = new Audio()
    audio.loop    = true
    audio.volume  = 0
    audio.muted   = mutedRef.current
    audio.preload = 'auto'
    audio.src     = src
    audio.load()
    audioRef.current = audio

    function startPlayback() {
      if (startedRef.current) return
      startedRef.current = true
      audio.muted = mutedRef.current
      audio.play().then(() => {
        removeListeners()
        if (!mutedRef.current) fadeIn(audio)
      }).catch(() => {
        startedRef.current = false
      })
    }

    function handleInteraction() { startPlayback() }

    function removeListeners() {
      window.removeEventListener('touchstart',  handleInteraction)
      window.removeEventListener('touchend',    handleInteraction)
      window.removeEventListener('pointerdown', handleInteraction)
      window.removeEventListener('keydown',     handleInteraction)
    }

    // Pause on tab-hidden, resume on return (works iOS 13+ and all desktop)
    function onVisibilityChange() {
      if (!startedRef.current) return
      if (document.hidden) {
        clearFade()
        audio.volume = 0
        audio.pause()
      } else if (!mutedRef.current) {
        audio.play().then(() => fadeIn(audio)).catch(() => {})
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('touchstart',  handleInteraction, { passive: true })
    window.addEventListener('touchend',    handleInteraction, { passive: true })
    window.addEventListener('pointerdown', handleInteraction, { passive: true })
    window.addEventListener('keydown',     handleInteraction)

    // Desktop: try immediately — no gesture restriction
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    if (!isIOS) startPlayback()

    return () => {
      removeListeners()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearFade()
      audio.pause()
      audio.src        = ''
      audioRef.current = null
      startedRef.current = false
    }
  }, [src, enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function clearFade() {
    if (fadeRef.current) {
      clearInterval(fadeRef.current)
      fadeRef.current = null
    }
  }

  function fadeIn(audio) {
    clearFade()
    if (mutedRef.current) return
    audio.volume = 0
    const steps        = 60
    const stepDuration = (fadeInDuration * 1000) / steps
    const volumeStep   = volume / steps
    let   step         = 0
    fadeRef.current = setInterval(() => {
      step++
      audio.volume = Math.min(volume, volumeStep * step)
      if (step >= steps) clearFade()
    }, stepDuration)
  }

  return null
}

export default AmbientMusic
